// The library (spec-v2.1.md B): sections (Ads with versions, Clips, Music, Voices, Product photos), filters by brand
// and project, search, newest first. Tap a thumbnail and it plays at once, full screen, in the same tap (no new
// screen, no keyboard, no second tap); swipe up or down for the next or previous one. Video cards show the render's
// poster and play its silent ~480p preview on hover (desktop) or press-and-hold (phones): components/PosterPreview.
//
// The v2 keyboard bug: tapping an item opened a sheet whose first focusable element was its Name field, and Sheet
// focused the first input it found, so the phone keyboard popped up over the video. Sheet no longer focuses text
// fields (components/bits.tsx), and the player here has no text field at all.
//
// v4 (spec-v4-structure-and-stage.md 3.3, 5.1, 7.1-7.4; ticket V4-A2): a library at every level. `?scope=` (account,
// channel:<id>, collection:<id>, project:<id>) picks the level; the scope chip names it ("Milo & Friends") with
// "Everything" one tap away, and "Only here" leaves out what belongs to the videos below. Roles are sections
// (Products, Places, Theme music, Intros and outros...). Auto tags and the description show on an item and are
// searched. An item's details rename it, set your tags, ask for the auto tags again, change its role and move it to
// another level. "Add files" (or a drop anywhere on the page, on desktop) opens the upload sheet (components/Upload).
import { useEffect, useMemo, useRef, useState, type DragEvent, type PointerEvent } from "react";
import { api, type StorageInfo } from "../api";
import { toggleAudio } from "../audio";
import { Empty, ErrorNote, Icon, Sheet, Spinner, timeAgo, useLoad } from "../components/bits";
import { Download } from "../components/Download";
import { PosterPreview } from "../components/PosterPreview";
import { Shell } from "../components/Shell";
import { StageHost } from "../components/Stage";
import { dragHasFiles, StorageMeter, UploadSheet } from "../components/Upload";
import { collections, type CollectionCard } from "../collections";
import { director } from "../director";
import {
  cleanTags,
  descriptionOf,
  durationOf,
  EVERYTHING,
  filterLibrary,
  fmtLength,
  groupSections,
  itemScope,
  libraryHref,
  parseScope,
  roleLabel,
  scopeChain,
  scopeKey,
  scopeName,
  scopeOptions,
  SECTIONS,
  sectionChips,
  sectionOf,
  stepIndex,
  swipeDir,
  USER_ROLES,
  versionOf,
  type LibScope,
  type LibSection,
  type ScopeNames,
} from "../library";
import { closePlayer, playNow } from "../player";

import { navigate, useLocation } from "../router";
import { toast, useRefresh, useStore } from "../store";
import type { Asset, Project } from "../types";

const WORD: Record<string, string> = { video: "Video", image: "Picture", voice: "Voiceover", music: "Music", sfx: "Sound effect", character: "Character", style: "Look", voice_preset: "Narrator", logo: "Logo" };
const isAudio = (a: Asset) => a.kind === "voice" || a.kind === "music" || a.kind === "sfx" || a.kind === "voice_preset";
const uploaded = (a: Asset) => a.origin === "upload" || a.origin === "suno";

function kindWord(a: Asset) {
  const sec = sectionOf(a);
  return roleLabel(a.kind, a.role) || (sec === "clips" ? "Clip" : sec === "ads" ? "Video" : WORD[a.kind]);
}
function metaLine(a: Asset, withKind = false) {
  const d = durationOf(a);
  const v = versionOf(a);
  return [withKind && kindWord(a), d && fmtLength(d), v && `v${v}`, uploaded(a) && (a.origin === "suno" ? "from Suno" : "uploaded"), timeAgo(a.createdAt)].filter(Boolean).join(", ");
}

/** The names the scope chip, "Where it lives" and the upload sheet's title need. */
function useScopeNames(scope: LibScope): ScopeNames {
  const channels = useStore((s) => s.me?.channels ?? []);
  const tick = useRefresh("collections");
  const { data: cols } = useLoad<CollectionCard[]>(() => collections.list().catch(() => []), [tick]);
  const { data: projects } = useLoad<Project[]>(() => api.projects().catch(() => []), []);
  const pid = scope.level === "project" ? scope.id : null;
  const { data: projectCollection } = useLoad<string | null>(() => (pid ? collections.videoSettings(pid).then((v) => v.collectionId, () => null) : Promise.resolve(null)), [pid]);
  return useMemo(
    () => ({
      channels: channels.map((c) => ({ id: c.id, name: c.name })),
      collections: (cols ?? []).map((c) => ({ id: c.id, name: c.name, channelId: c.channelId })),
      projects: (projects ?? []).map((p) => ({ id: p.id, title: p.title, channelId: p.channelId })),
      projectCollection,
    }),
    [channels, cols, projects, projectCollection],
  );
}

export function LibraryScreen() {
  const { query } = useLocation();
  const section = (query.get("s") ?? "") as LibSection | "";
  const scope = parseScope(query.get("scope"));
  const here = scope.level !== "account" && (query.get("here") === "1" || query.get("here") === "true");
  const key = scopeKey(scope);
  const [q, setQ] = useState(query.get("q") ?? "");
  const tick = useRefresh("library");
  const { data, error, loading, reload } = useLoad(() => api.library(scope.level === "account" ? {} : { scope: key, here }), [tick, key, here]);
  const { data: storage, setData: setStorage } = useLoad<StorageInfo | null>(() => api.myStorage().catch(() => null), [tick]);
  const names = useScopeNames(scope);
  const [open, setOpen] = useState<number | null>(null);
  const [details, setDetails] = useState<Asset | null>(null);
  const [adding, setAdding] = useState<File[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const drags = useRef(0);

  const searched = useMemo(() => filterLibrary(data ?? [], { q }), [data, q]);
  const shown = useMemo(() => (section ? searched.filter((a) => sectionOf(a) === section) : searched), [searched, section]);
  const groups = useMemo(() => (section ? [{ section: SECTIONS.find((s) => s.id === section)!, items: shown }] : groupSections(shown)), [section, shown]);
  const chips = useMemo(() => sectionChips(data ?? [], section), [data, section]);
  // the player walks the items in the order they're shown
  const order = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const meta = SECTIONS.find((s) => s.id === section);
  const filtered = !!q.trim();
  const chain = scopeChain(scope, names);
  const name = scopeName(scope, names);
  const go = (s: LibScope, o: { section?: LibSection | ""; here?: boolean } = {}) => navigate(libraryHref(s, { section: o.section ?? section, here: o.here ?? here }), { replace: true });

  useEffect(() => {
    if (scope.level !== "account") director.context(`The user is looking at the library of ${name} (${key})${here ? ", only what's added there itself" : ""}.`);
  }, [key, here, name]); // eslint-disable-line react-hooks/exhaustive-deps

  // a drop anywhere on the page (desktop) opens the upload sheet with those files
  const dragIn = (e: DragEvent) => {
    if (!dragHasFiles(e) || adding) return;
    e.preventDefault();
    if (e.type === "dragenter") drags.current++;
    setDragging(true);
  };
  const dragOut = () => {
    drags.current = Math.max(0, drags.current - 1);
    if (!drags.current) setDragging(false);
  };
  const dropped = (e: DragEvent) => {
    if (!dragHasFiles(e) || adding) return;
    e.preventDefault();
    drags.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) setAdding(files);
  };

  return (
    <Shell>
      <div className={`page library lib${dragging ? " is-dropping" : ""}`} onDragEnter={dragIn} onDragOver={dragIn} onDragLeave={dragOut} onDrop={dropped}>
        <header className="page-head lib-head">
          <div className="lib-title-row">
            <h1 className="page-title">Library</h1>
            <button data-action="library.add-files" type="button" className="btn btn-primary btn-small lib-add" onClick={() => setAdding([])}>
              <Icon name="plus" size={16} /> Add files
            </button>
          </div>
          <p className="page-lede">
            {scope.level === "account"
              ? "Everything you and your Director have made, and the files you add. Tap anything to play it."
              : here
                ? `Only what's added to ${name} itself.`
                : `Everything for ${name}${scope.level === "project" ? "" : " and its videos"}. Tap anything to play it.`}
          </p>
        </header>

        <div className="lib-tools">
          <nav className="lib-scope" aria-label="Which library">
            <ScopeChip scope={scope} label={chain.length > 1 ? chain.at(-1)!.label : "Everything"} names={names} onPick={(s) => go(s, { here: false })} />
            {scope.level !== "account" && (
              <>
                {chain.slice(1, -1).reverse().map((c) => (
                  <button data-action="library.scope" key={scopeKey(c.scope)} type="button" className="chip lib-up" onClick={() => go(c.scope, { here: false })}>
                    {c.label}
                  </button>
                ))}
                <button data-action="library.scope" type="button" className="chip" onClick={() => go(EVERYTHING, { here: false })}>
                  Everything
                </button>
                <button data-action="library.scope" type="button" className={`chip lib-here${here ? " is-on" : ""}`} aria-pressed={here} onClick={() => go(scope, { here: !here })}>
                  {here && <Icon name="check" size={14} />} Only here
                </button>
              </>
            )}
          </nav>
          {chips.length > 1 && (
            <nav className="lib-sections" aria-label="Sections">
              {[{ id: "" as const, label: "All" }, ...chips].map((s) => (
                <button key={s.id || "all"} type="button" aria-pressed={s.id === section} className={`chip${s.id === section ? " is-on" : ""}`} onClick={() => go(scope, { section: s.id })}>
                  {s.label}
                </button>
              ))}
            </nav>
          )}
          <div className="lib-filters">
            <label className="search">
              <Icon name="search" size={18} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names and tags" aria-label="Search the library" enterKeyHint="search" autoComplete="off" />
            </label>
          </div>
          {storage && <StorageMeter info={storage} className="lib-meter" />}
        </div>

        {error && <ErrorNote text={`Couldn't load the library. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading" />}
        {data && !shown.length && (
          <Empty
            title={filtered ? "Nothing matches" : scope.level !== "account" && !section ? `Nothing in ${name} yet` : `No ${meta ? meta.label.toLowerCase() : "items"} yet`}
            action={
              !filtered && (
                <button data-action="library.add-files" type="button" className="btn btn-quiet" onClick={() => setAdding([])}>
                  Add files
                </button>
              )
            }
          >
            {filtered ? "Try fewer words, or clear the search." : (meta?.empty ?? "Everything your Director makes lands here, and anything you add.")}
          </Empty>
        )}
        {groups.map((g) => (
          <section key={g.section.id} className="lib-group" aria-labelledby={`lib-${g.section.id}`}>
            {!section && (
              <header className="section-head">
                <h2 id={`lib-${g.section.id}`}>{g.section.label}</h2>
                <span className="muted">{g.items.length}</span>
              </header>
            )}
            <ul className={`lib-grid lib-grid-${g.section.id}`}>
              {g.items.map((a) => (
                <li key={a.id} className="lib-cell">
                  <Tile a={a} onOpen={() => setOpen(order.indexOf(a))} />
                  <button type="button" className="ltile-dots" onClick={() => setDetails(a)} aria-label={`Details for ${a.name}`} title="Details">
                    <Icon name="more" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {dragging && (
          <div className="lib-dropzone" aria-hidden>
            <span>Drop to add to {name}</span>
          </div>
        )}
      </div>
      {open !== null && order[open] && <LibraryPlayer items={order} index={open} onIndex={setOpen} onClose={() => setOpen(null)} onDetails={setDetails} />}
      {details && (
        <ItemSheet
          a={details}
          names={names}
          onClose={() => setDetails(null)}
          onSaved={(n, what) => {
            setDetails(null);
            reload();
            toast(what, "good");
            if (open !== null) setOpen(Math.min(open, Math.max(0, order.length - 1)));
            void n;
          }}
        />
      )}
      {adding && (
        <UploadSheet
          scope={scope}
          scopeName={name}
          files={adding}
          onClose={() => {
            setAdding(null);
            reload();
          }}
          onDone={(items) => {
            const bytes = items.reduce((n, a) => n + (a.bytes ?? 0), 0);
            if (storage && bytes) setStorage({ ...storage, usedBytes: storage.usedBytes + bytes, termsAccepted: true });
          }}
          onEdit={(a) => {
            setAdding(null);
            reload();
            setDetails(a);
          }}
        />
      )}
    </Shell>
  );
}

/** The level being shown, as a chip that opens a picker of every level (a native select: the phone's own list). */
function ScopeChip({ scope, label, names, onPick }: { scope: LibScope; label: string; names: ScopeNames; onPick: (s: LibScope) => void }) {
  const groups = scopeOptions(names);
  const key = scopeKey(scope);
  const known = groups.some((g) => g.options.some((o) => o.key === key));
  return (
    <label className="chip is-on lib-scope-chip">
      <span className="lib-scope-label">{label}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
        <path d="m7 10 5 5 5-5" />
      </svg>
      <select value={key} onChange={(e) => onPick(parseScope(e.target.value))} aria-label="Show the library of">
        {!known && <option value={key}>{label}</option>}
        {groups.map((g) =>
          g.group ? (
            <optgroup key={g.group} label={g.group}>
              {g.options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ) : (
            g.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.key === "account" ? "Everything" : o.label}
              </option>
            ))
          ),
        )}
      </select>
    </label>
  );
}

const visualOf = (a: Asset) => a.thumbUrl ?? (a.kind === "image" || a.kind === "logo" || a.kind === "character" || a.kind === "style" ? a.url : null);

function Tile({ a, onOpen }: { a: Asset; onOpen: () => void }) {
  const sec = sectionOf(a);
  const audio = isAudio(a);
  return (
    <button type="button" className={`ltile ltile-${sec}`} onClick={onOpen} disabled={!a.url && !visualOf(a)} aria-label={`Play ${a.name}`}>
      <span className="ltile-media">
        {a.kind === "video" ? <VideoThumb a={a} /> : audio ? <Wave seed={a.id} /> : visualOf(a) ? <img src={visualOf(a)!} alt="" loading="lazy" /> : <Icon name="library" size={24} />}
        {(a.kind === "video" || audio) && (
          <span className="ltile-play" aria-hidden>
            <Icon name="play" size={14} />
          </span>
        )}
        {versionOf(a) && <span className="ltile-ver">v{versionOf(a)}</span>}
        {durationOf(a) && <span className="ltile-len">{fmtLength(durationOf(a)!)}</span>}
      </span>
      <span className="ltile-name">{a.name}</span>
      <span className="ltile-meta">{[!versionOf(a) && sec === "ads" ? "Video" : roleLabel(a.kind, a.role) || null, uploaded(a) ? "yours" : null, timeAgo(a.createdAt)].filter(Boolean).join(", ")}</span>
    </button>
  );
}

/** A video card: the render's poster (a clip: its frame); hover or press-and-hold plays the silent preview. A video
 *  made before previews existed (not backfilled yet) previews its full file, muted, as before. */
function VideoThumb({ a }: { a: Asset }) {
  return <PosterPreview className="vthumb" poster={a.thumbUrl} preview={a.previewUrl ?? a.url} blank={<span className="vthumb-blank" />} />;
}

function Wave({ seed }: { seed: string }) {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const bars = Array.from({ length: 32 }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    return 0.18 + ((h >>> 16) / 65535) * 0.82 * (0.45 + 0.55 * Math.abs(Math.sin(i / 3.2)));
  });
  return (
    <svg viewBox="0 0 128 40" className="wave ltile-wave" aria-hidden preserveAspectRatio="none">
      {bars.map((v, i) => (
        <rect key={i} x={i * 4} y={20 - v * 18} width="2.2" height={v * 36} rx="1.1" />
      ))}
    </svg>
  );
}

// ------------------------------------------------------------------ the full-screen player

function LibraryPlayer({ items, index, onIndex, onClose, onDetails }: { items: Asset[]; index: number; onIndex: (i: number) => void; onClose: () => void; onDetails: (a: Asset) => void }) {
  const a = items[index];
  const playing = useStore((s) => s.playing === `lib-${a.id}`);
  const start = useRef<{ x: number; y: number; t: number } | null>(null);
  const [dir, setDir] = useState<"next" | "prev" | null>(null);
  const isVideo = a.kind === "video";

  // play the current item straight away: the tap (or swipe) that got here is the gesture
  useEffect(() => {
    if (isVideo && a.url) playNow({ url: a.url, title: a.name, what: sectionOf(a) === "ads" ? "render" : "clip", projectId: a.projectId ?? undefined, assetId: a.id, version: versionOf(a), surface: "library" });
    else closePlayer();
    if ((a.kind === "voice" || a.kind === "music" || a.kind === "sfx") && a.url) toggleAudio(`lib-${a.id}`, a.url);
    director.context(`The user is looking at "${a.name}" in the library, full screen.`);
  }, [a.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.classList.add("is-locked");
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowDown" || e.key === "ArrowRight") go("next");
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") go("prev");
    };
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("keydown", key);
      document.body.classList.remove("is-locked");
    };
  });

  const close = () => {
    closePlayer();
    if (playing) toggleAudio(`lib-${a.id}`, a.url);
    onClose();
  };
  const go = (d: "next" | "prev") => {
    const n = stepIndex(index, items.length, d);
    if (n === index) return;
    if (playing) toggleAudio(`lib-${a.id}`, a.url);
    setDir(d);
    onIndex(n);
  };
  const down = (e: PointerEvent) => (start.current = { x: e.clientX, y: e.clientY, t: performance.now() });
  const up = (e: PointerEvent) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const d = swipeDir(e.clientX - s.x, e.clientY - s.y, performance.now() - s.t);
    if (d) go(d);
  };

  return (
    <div className="lplayer" role="dialog" aria-modal="true" aria-label={a.name}>
      <header className="lplayer-top">
        <button type="button" className="icon-btn" onClick={close} aria-label="Close">
          <Icon name="close" />
        </button>
        <p className="lplayer-count">
          {index + 1} of {items.length}
        </p>
        <button type="button" className="icon-btn" onClick={() => onDetails(a)} aria-label="Details, tags and where it lives">
          <Icon name="edit" />
        </button>
      </header>
      <div className={`lplayer-stage${dir ? ` lplayer-from-${dir}` : ""}`} key={a.id} onPointerDown={down} onPointerUp={up}>
        {isVideo ? (
          <StageHost poster={a.thumbUrl ?? undefined} />
        ) : isAudio(a) ? (
          <button type="button" className={`lplayer-audio${playing ? " is-on" : ""}`} onClick={() => toggleAudio(`lib-${a.id}`, a.url)} aria-label={playing ? `Pause ${a.name}` : `Play ${a.name}`} disabled={!a.url}>
            <Wave seed={a.id} />
            <span className="lplayer-audio-btn">
              <Icon name={playing ? "pause" : "play"} size={28} />
            </span>
          </button>
        ) : (
          <img src={visualOf(a) ?? ""} alt={a.name} />
        )}
      </div>
      <footer className="lplayer-foot">
        <div>
          <p className="lplayer-name">{a.name}</p>
          <p className="muted">{metaLine(a, true)}</p>
          {descriptionOf(a) && <p className="lplayer-desc">{descriptionOf(a)}</p>}
        </div>
        <div className="lplayer-acts">
          {a.projectId && (
            <button data-action="video.open" type="button" className="btn btn-quiet btn-small" onClick={() => (close(), navigate(`/p/${a.projectId}`))}>
              Open its project
            </button>
          )}
          <Download url={a.downloadUrl ?? a.url} label={a.name} variant="button" />
        </div>
        <div className="lplayer-nav">
          <button type="button" className="icon-btn" onClick={() => go("prev")} disabled={index === 0} aria-label="Previous">
            <span className="flip">
              <Icon name="chevron" size={20} />
            </span>
          </button>
          <button type="button" className="icon-btn" onClick={() => go("next")} disabled={index === items.length - 1} aria-label="Next">
            <Icon name="chevron" size={20} />
          </button>
        </div>
      </footer>
      <p className="lplayer-hint" aria-hidden>
        Swipe up for the next one
      </p>
    </div>
  );
}

/** An item's details: what it is, its tags (yours, plus the auto tags with "Tag again"), its description; rename,
 *  your tags, its role and where it lives, saved together (PATCH /api/library/:id); and its Download. */
function ItemSheet({ a, names, onClose, onSaved }: { a: Asset; names: ScopeNames; onClose: () => void; onSaved: (a: Asset, what: string) => void }) {
  const [name, setName] = useState(a.name);
  const [tags, setTags] = useState<string[]>(a.tags);
  const [draft, setDraft] = useState("");
  const [role, setRole] = useState<string>(a.role ?? "");
  const [where, setWhere] = useState(scopeKey(itemScope(a)));
  const [busy, setBusy] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [auto, setAuto] = useState<string[]>(a.autoTags ?? []);
  const roles = USER_ROLES[a.kind] ?? [];
  const groups = scopeOptions(names);
  const was = scopeKey(itemScope(a));
  const known = groups.some((g) => g.options.some((o) => o.key === was));

  // "Tag again": the new auto tags arrive as an "asset" event, which bumps the library; read this item again then
  const tick = useRefresh("library");
  useEffect(() => {
    if (!retagging) return;
    let live = true;
    api
      .library({ scope: was, here: true })
      .then((items) => {
        const n = items.find((x) => x.id === a.id);
        if (live && n && (n.autoTags ?? []).join() !== auto.join()) {
          setAuto(n.autoTags ?? []);
          setRetagging(false);
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const allTags = cleanTags([...tags, ...(draft.trim() ? cleanTags(draft) : [])]);
  const changes = {
    ...(name.trim() && name.trim() !== a.name ? { name: name.trim() } : {}),
    ...(allTags.join() !== a.tags.join() ? { tags: allTags } : {}),
    ...(roles.length && role !== (a.role ?? "") ? { role: role || null } : {}),
    ...(where !== was ? { scope: where } : {}),
  };
  const changed = Object.keys(changes).length > 0;
  const whereLabel = (k: string) => groups.flatMap((g) => g.options).find((o) => o.key === k)?.label ?? "another place";

  const save = async () => {
    if (!changed || busy) return;
    setBusy(true);
    try {
      const n = await api.patchLibrary(a.id, changes);
      const said = changes.scope ? `Moved to ${changes.scope === "account" ? "Everything" : whereLabel(changes.scope)}.` : changes.name ? `Renamed to “${n.name}”.` : "Saved.";
      director.context(`The user edited "${n.name}" in the library${changes.scope ? `, moved to ${changes.scope}` : ""}.`);
      onSaved(n, said);
    } catch (err) {
      toast(`Couldn't save. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };
  const retag = async () => {
    setRetagging(true);
    try {
      await api.retagLibrary(a.id);
      toast("Looking at it again. New tags in a moment.", "good");
    } catch (err) {
      setRetagging(false);
      toast(`Couldn't tag it again. ${(err as Error).message}`, "bad");
    }
  };
  const addDraft = () => {
    if (!draft.trim()) return;
    setTags(cleanTags([...tags, ...cleanTags(draft)]));
    setDraft("");
  };

  return (
    <Sheet title="Details" onClose={onClose} className="item-sheet">
      <div className="sheet-body item-body">
        <p className="muted">{metaLine(a, true)}</p>
        {descriptionOf(a) && <p className="item-desc">{descriptionOf(a)}</p>}

        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </label>

        <div className="field">
          <span className="field-label" id={`tags-${a.id}`}>
            Your tags
          </span>
          <ul className="item-tags" aria-labelledby={`tags-${a.id}`}>
            {tags.map((t) => (
              <li key={t} className="tag">
                {t}
                <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} aria-label={`Remove the tag ${t}`}>
                  <Icon name="close" size={12} />
                </button>
              </li>
            ))}
            <li className="item-tag-add">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addDraft();
                  }
                }}
                onBlur={addDraft}
                placeholder={tags.length ? "Add a tag" : "Add tags, like warm or kitchen"}
                aria-label="Add a tag"
                enterKeyHint="done"
              />
            </li>
          </ul>
        </div>

        <div className="field">
          <span className="field-label">Auto tags</span>
          <div className="item-auto">
            {auto.length ? (
              <ul className="item-tags is-auto">
                {auto.map((t) => (
                  <li key={t} className="tag">
                    {t}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">{retagging ? "Looking at it…" : "None yet."}</span>
            )}
            <button data-action="library.retag" type="button" className="btn btn-quiet btn-small" onClick={retag} disabled={retagging}>
              <Icon name="redo" size={14} /> {retagging ? "Tagging…" : "Tag again"}
            </button>
          </div>
        </div>

        {!!roles.length && (
          <label className="field select-field">
            <span className="field-label">What it is</span>
            <span className="select">
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">{a.kind === "image" ? "A picture" : a.kind === "music" ? "Music" : "A clip"}</option>
                {roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.label}
                  </option>
                ))}
              </select>
            </span>
          </label>
        )}

        <label className="field select-field">
          <span className="field-label">Where it lives</span>
          <span className="select">
            <select value={where} onChange={(e) => setWhere(e.target.value)}>
              {!known && <option value={was}>Where it is now</option>}
              {groups.map((g) =>
                g.group ? (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  g.options.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))
                ),
              )}
            </select>
          </span>
        </label>

        <div className="item-acts">
          <button data-action="library.save" type="button" className="btn btn-primary" onClick={save} disabled={!changed || busy}>
            {busy ? "Saving…" : "Save"}
          </button>
          <Download url={a.downloadUrl ?? a.url} label={a.name} variant="button" />
          {a.projectId && (
            <button data-action="video.open" type="button" className="btn btn-quiet btn-small" onClick={() => (onClose(), navigate(`/p/${a.projectId}`))}>
              Open its video
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
